import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "axios";
import "../assets/style.css";
import "../assets/singleProduct.css";
import "../assets/shop.css";

interface ColorImage {
  id: number;
  image_path: string;
  sort_order?: number;
}

interface ColorVariant {
  id: number;
  color_name: string;
  images: ColorImage[];
}

interface ProductData {
  id: number;
  code: string;
  barCode: string;
  SKU: string;
  vendor_id: number;
  vendorId: number;
  productName: string;
  UOM: number;
  product_condition: string;
  categoryId: number;
  subcategoryId: number;
  productType: number;
  cost: string;
  actualPrice: number;
  previousPrice: number;
  sellingPrice: number;
  activePrice: string;
  active_price: number;
  total_sold: number;
  view_count: number;
  is_featured: number;
  is_promoted: number;
  status: string;
  created_at: string;
  updated_at: string;
  markUp: number;
  shortDescription: string;
  longDescription: string;
  variant: string;
  quantity: number;
  brand_name: string;
  model: string;
  stock_status: string;
  productVideo?: string | null;
  colorVariants: ColorVariant[];
}

interface ProductReviewMedia {
  id: number;
  reviewId: number;
  mediaType: "photo" | "video";
  filePath: string;
  originalName: string;
}

interface ProductReview {
  id: number;
  orderId: string;
  product_id: number;
  buyerName: string;
  rating: number;
  comment: string;
  userImage: string;
  createdAt: string;
  media: ProductReviewMedia[];
}

interface RelatedProduct {
  id: number;
  barCode: string;
  vendor_id: number;
  productName: string;
  variant: string;
  shortDescription: string;
  active_price: number;
  image_path: string;
  stock_status?: string;
}

interface FreeGiftProduct {
  id: number;
  productName: string;
}

interface DiscountVoucherData {
  id: number;
  title: string;
  code: string;
  description?: string;
  voucher_type: "discount" | "free_gift" | "buy_one_get_one";
  discount_type?: "fixed" | "percent";
  discount_value?: number;
  min_product_quantity?: number;
  min_product_amount?: number;
  max_discount_amount?: number | null;
  free_gift_quantity?: number;
  buy_quantity?: number;
  free_quantity?: number;
  freeGiftProducts?: FreeGiftProduct[];
}

const PRODUCT_FILE_BASE =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000";
const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";
const FILE_BASE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000";

const SingleProduct: React.FC = () => {
  const [product, setProduct] = useState<ProductData | null>(null);
  const [selectedColorId, setSelectedColorId] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [selectedMediaType, setSelectedMediaType] = useState<"video" | "image">(
    "video",
  );
  const [selectedQty, setSelectedQty] = useState<number>(1);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState<boolean>(false);
  const [reviewStartIndex, setReviewStartIndex] = useState<number>(0);
  const [visibleReviewCount, setVisibleReviewCount] = useState<number>(3);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [relatedLoading, setRelatedLoading] = useState<boolean>(false);
  const [relatedStartIndex, setRelatedStartIndex] = useState<number>(0);
  const [visibleRelatedCount, setVisibleRelatedCount] = useState<number>(4);
  const [productVouchers, setProductVouchers] = useState<DiscountVoucherData[]>(
    [],
  );
  const relatedTouchStartX = useRef<number | null>(null);
  const relatedTouchEndX = useRef<number | null>(null);

  const location = useLocation();
  const { addToCart } = useCart();
  const params = new URLSearchParams(location.search);
  const productId = params.get("id");

  const handleRelatedTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    relatedTouchStartX.current = e.touches[0].clientX;
  };

  const handleRelatedTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    relatedTouchEndX.current = e.touches[0].clientX;
  };

  const handleRelatedTouchEnd = () => {
    if (
      relatedTouchStartX.current === null ||
      relatedTouchEndX.current === null
    ) {
      return;
    }

    const distance = relatedTouchStartX.current - relatedTouchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        handleNextRelated();
      } else {
        handlePrevRelated();
      }
    }

    relatedTouchStartX.current = null;
    relatedTouchEndX.current = null;
  };

  const formatCurrency = (value: any) => {
    const num = Number(value);
    if (isNaN(num)) return "";

    return num.toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/singleproducts/${productId}`,
        );

        const fetchedProduct: ProductData | null =
          response.data?.singleProduct || null;
        setProduct(fetchedProduct);

        if (fetchedProduct?.colorVariants?.length) {
          const firstColor = fetchedProduct.colorVariants[0];
          setSelectedColorId(firstColor.id);

          if (fetchedProduct.productVideo) {
            setSelectedMediaType("video");
            setSelectedImage("");
          } else if (firstColor.images?.length) {
            setSelectedMediaType("image");
            setSelectedImage(firstColor.images[0].image_path);
          }
        } else if (fetchedProduct?.productVideo) {
          setSelectedMediaType("video");
          setSelectedImage("");
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!product?.id) return;

      try {
        setReviewsLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/reviews/product/${product.id}`,
        );
        setReviews(response.data?.data || []);
      } catch (error) {
        console.error("fetchReviews error:", error);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [product?.id]);

  useEffect(() => {
    const updateVisibleReviewCount = () => {
      if (window.innerWidth <= 575) {
        setVisibleReviewCount(1);
      } else if (window.innerWidth <= 991) {
        setVisibleReviewCount(2);
      } else {
        setVisibleReviewCount(3);
      }
    };

    updateVisibleReviewCount();
    window.addEventListener("resize", updateVisibleReviewCount);

    return () => window.removeEventListener("resize", updateVisibleReviewCount);
  }, []);

  useEffect(() => {
    setReviewStartIndex(0);
  }, [reviews.length, visibleReviewCount]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product?.subcategoryId) return;

      try {
        setRelatedLoading(true);

        const response = await axios.get(
          `${import.meta.env.VITE_LYRAZONE_BASE_URL}/subproducts/${product.subcategoryId}`,
        );

        const products = response.data?.subProduct || [];

        const filtered = products.filter(
          (item: RelatedProduct) => Number(item.id) !== Number(product.id),
        );

        setRelatedProducts(filtered);
        setRelatedStartIndex(0);
      } catch (error) {
        console.error("fetchRelatedProducts error:", error);
        setRelatedProducts([]);
      } finally {
        setRelatedLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [product?.subcategoryId, product?.id]);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth <= 480) {
        setVisibleRelatedCount(4);
      } else if (window.innerWidth <= 767) {
        setVisibleRelatedCount(2);
      } else if (window.innerWidth <= 1199) {
        setVisibleRelatedCount(3);
      } else {
        setVisibleRelatedCount(4);
      }
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);

    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  useEffect(() => {
    const fetchProductVouchers = async () => {
      if (!product?.id) {
        setProductVouchers([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/discount-vouchers/product/${product.id}`,
        );

        const rows =
          response.data?.data ||
          response.data?.discountVouchers ||
          response.data ||
          [];
        setProductVouchers(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("fetchProductVouchers error:", error);
        setProductVouchers([]);
      }
    };

    fetchProductVouchers();
  }, [product?.id]);

  const selectedColor = useMemo(() => {
    if (!product?.colorVariants?.length) return null;

    return (
      product.colorVariants.find((color) => color.id === selectedColorId) ||
      product.colorVariants[0]
    );
  }, [product, selectedColorId]);

  const visibleImages = useMemo(() => {
    if (!product?.colorVariants?.length) return [];

    if (!selectedColor) {
      return product.colorVariants.flatMap((color) => color.images || []);
    }

    return selectedColor.images || [];
  }, [product, selectedColor]);

  const averageRating = useMemo(() => {
    if (!reviews.length) return 0;
    const total = reviews.reduce(
      (sum, review) => sum + Number(review.rating || 0),
      0,
    );
    return total / reviews.length;
  }, [reviews]);

  // const reviewSummaryText = useMemo(() => {
  //   if (!reviews.length) return "No reviews yet";
  //   return `${reviews.length} review${reviews.length > 1 ? "s" : ""}`;
  // }, [reviews]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      const checked = starNumber <= Math.round(rating);

      return (
        <span
          key={starNumber}
          className={`fa fa-star ${checked ? "checked" : ""}`}
        ></span>
      );
    });
  };

  const getReviewTitle = (review: ProductReview) => {
    if (review.comment?.trim()) {
      const firstSentence = review.comment.trim().split(".")[0].trim();
      return firstSentence.length > 60
        ? `${firstSentence.slice(0, 60)}...`
        : firstSentence;
    }

    if (review.rating === 5) return "Excellent product";
    if (review.rating === 4) return "Very good product";
    if (review.rating === 3) return "Good product";
    if (review.rating === 2) return "Average product";
    return "Needs improvement";
  };

  const goToProduct = (id: number) => {
    window.location.href = `/singleproduct?id=${id}`;
  };

  const decreaseSelectedQty = () => {
    setSelectedQty((prev) => Math.max(prev - 1, 1));
  };

  const increaseSelectedQty = () => {
    setSelectedQty((prev) => prev + 1);
  };

  useEffect(() => {
    setSelectedQty(1);
  }, [product?.id]);

  const getVoucherHeadline = (voucher: DiscountVoucherData) => {
    if (voucher.voucher_type === "free_gift") {
      const gifts =
        voucher.freeGiftProducts?.map((gift) => gift.productName).join(", ") ||
        "Free Gift Item";

      return `${voucher.title} (${voucher.code}) - Free Gift: ${gifts} x ${
        voucher.free_gift_quantity || 1
      }`;
    }

    if (voucher.voucher_type === "buy_one_get_one") {
      return `${voucher.title} (${voucher.code}) - Buy ${
        voucher.buy_quantity || 1
      } Get ${voucher.free_quantity || 1} Free`;
    }

    if (voucher.discount_type === "percent") {
      return `${voucher.title} (${voucher.code}) - ${
        Number(voucher.discount_value || 0) || 0
      }% OFF`;
    }

    return `${voucher.title} (${voucher.code}) - RM ${formatCurrency(
      voucher.discount_value || 0,
    )} OFF`;
  };

  const visibleVoucherTickerItems = useMemo(() => {
    if (!product) return [];

    return productVouchers.filter((voucher) => {
      const minQty = Number(voucher.min_product_quantity || 1);
      const minAmount = Number(voucher.min_product_amount || 0);

      return (
        selectedQty >= minQty &&
        Number(product.active_price || 0) * selectedQty >= minAmount
      );
    });
  }, [productVouchers, product, selectedQty]);

  const voucherTickerText = useMemo(() => {
    const source =
      visibleVoucherTickerItems.length > 0
        ? visibleVoucherTickerItems
        : productVouchers;

    if (!source.length) return "";

    const joined = source.map(getVoucherHeadline).join("  •  ");

    return `${joined}  •  ${joined}`;
  }, [visibleVoucherTickerItems, productVouchers]);

  if (!product) {
    return (
      <>
        <div id="home-p" className="home-p pages-head10 text-center">
          {/* <div className="container">
            <h1 className="wow fadeInUp" data-wow-delay="0.1s">
              Product Details
            </h1>
          </div> */}
        </div>

        <section id="single-product-p1">
          <div className="container">
            <p>Loading product...</p>
          </div>
        </section>
      </>
    );
  }

  const showActualPrice =
    (product.activePrice === "selling" || product.activePrice === "previous") &&
    Number(product.actualPrice) > Number(product.active_price);

  const discount =
    showActualPrice && Number(product.actualPrice) > 0
      ? Math.round(
          ((Number(product.actualPrice) - Number(product.active_price)) /
            Number(product.actualPrice)) *
            100,
        )
      : 0;

  const isSoldOut = String(product.stock_status).toLowerCase() === "soldout";

  const visibleReviews = reviews.slice(
    reviewStartIndex,
    reviewStartIndex + visibleReviewCount,
  );

  const canGoPrevReview = reviewStartIndex > 0;
  const canGoNextReview =
    reviewStartIndex + visibleReviewCount < reviews.length;

  const handlePrevReview = () => {
    if (!canGoPrevReview) return;
    setReviewStartIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextReview = () => {
    if (!canGoNextReview) return;
    setReviewStartIndex((prev) =>
      Math.min(prev + 1, reviews.length - visibleReviewCount),
    );
  };

  const visibleRelatedProducts = relatedProducts.slice(
    relatedStartIndex,
    relatedStartIndex + visibleRelatedCount,
  );

  const canGoPrev = relatedStartIndex > 0;
  const canGoNext =
    relatedStartIndex + visibleRelatedCount < relatedProducts.length;

  const handlePrevRelated = () => {
    if (!canGoPrev) return;
    setRelatedStartIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextRelated = () => {
    if (!canGoNext) return;
    setRelatedStartIndex((prev) =>
      Math.min(prev + 1, relatedProducts.length - visibleRelatedCount),
    );
  };

  return (
    <>
      <div id="home-p" className="home-p pages-head10 text-center">
        {/* <div className="container">
          <h1 className="wow fadeInUp" data-wow-delay="0.1s">
            Product Details
          </h1>
        </div> */}
      </div>

      <section id="single-product-p1">
        <div className="container">
          <div className="wrapper row">
            <div className="preview col-md-6">
              <div
                className="zoom"
                style={{ maxWidth: "100%", height: "auto" }}
              >
                <div className="preview-pic tab-content">
                  {selectedMediaType === "video" && product.productVideo ? (
                    <div className="tab-pane active">
                      <video
                        controls
                        style={{ width: "100%" }}
                        src={`${PRODUCT_FILE_BASE}/uploads/products/${product.id}/${product.productVideo}`}
                      />
                    </div>
                  ) : selectedImage ? (
                    <div className="tab-pane active">
                      <img
                        src={`${PRODUCT_FILE_BASE}/uploads/products/${product.id}/${selectedImage}`}
                        alt={product.productName}
                        style={{ width: "100%" }}
                      />
                    </div>
                  ) : (
                    <div className="tab-pane active">
                      <p>No preview available.</p>
                    </div>
                  )}
                </div>
              </div>

              <ul className="preview-thumbnail nav nav-tabs">
                {product.productVideo && (
                  <li
                    className={selectedMediaType === "video" ? "active" : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedMediaType("video");
                      setSelectedImage("");
                    }}
                  >
                    <a>
                      <video
                        muted
                        style={{ width: "100%" }}
                        src={`${PRODUCT_FILE_BASE}/uploads/products/${product.id}/${product.productVideo}`}
                      />
                    </a>
                  </li>
                )}

                {visibleImages.map((img, index) => (
                  <li
                    key={`${img.id}-${index}`}
                    className={
                      selectedMediaType === "image" &&
                      selectedImage === img.image_path
                        ? "active"
                        : ""
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedMediaType("image");
                      setSelectedImage(img.image_path);
                    }}
                  >
                    <a>
                      <img
                        src={`${PRODUCT_FILE_BASE}/uploads/products/${product.id}/${img.image_path}`}
                        alt={`Thumbnail ${index + 1}`}
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="details col-md-6">
              <h3 className="product-title">{product.productName}</h3>
              {/* <div className="rating">
                <div className="stars">{renderStars(averageRating)}</div>
                <span className="review-no">{reviewSummaryText}</span>
              </div> */}
              <div className="product-rating-summary">
                <div className="rating-score">
                  {reviews.length ? averageRating.toFixed(1) : "0.0"}
                </div>

                <div className="rating-details">
                  <div className="rating-stars">
                    {renderStars(averageRating)}
                  </div>

                  <div className="rating-text">
                    {reviews.length > 0
                      ? `${reviews.length} review${reviews.length > 1 ? "s" : ""}`
                      : "No reviews yet"}
                  </div>
                </div>
              </div>
              <p className="product-description">{product.shortDescription}</p>
              {showActualPrice && (
                <>
                  <strong>Actual Price</strong>
                  <p
                    style={{
                      color: "#888",
                      textDecoration: "line-through",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    RM {formatCurrency(product.actualPrice)}
                  </p>
                </>
              )}
              <h4 className="price">
                current price{" "}
                <span>
                  RM {formatCurrency(product.active_price)}
                  {showActualPrice && (
                    <span
                      style={{
                        color: "green",
                        fontWeight: "bold",
                        marginLeft: "10px",
                        fontSize: "14px",
                      }}
                    >
                      {discount}% OFF
                    </span>
                  )}
                </span>
              </h4>
              <div className="product-meta-inline">
                <span
                  className={`product-condition-badge ${
                    String(product.product_condition).toLowerCase() === "new"
                      ? "condition-new"
                      : "condition-used"
                  }`}
                >
                  {product.product_condition.toUpperCase()}
                </span>

                <span className="product-meta-separator">|</span>

                <div className="qty-box product-qty-box">
                  <button type="button" onClick={decreaseSelectedQty}>
                    -
                  </button>
                  <span>{selectedQty}</span>
                  <button type="button" onClick={increaseSelectedQty}>
                    +
                  </button>
                </div>

                <span className="product-meta-separator">|</span>

                <div className="product-sold-inline">
                  <strong>{Number(product.total_sold || 0)}</strong>
                  <span>Sold</span>
                  <i
                    className="fa fa-question-circle-o product-sold-icon"
                    aria-hidden="true"
                    title="Total units sold"
                  ></i>
                </div>
              </div>
              {!!product.colorVariants?.length && (
                <>
                  <p className="colors">Variants</p>
                  <div className="product-color-swatches">
                    {product.colorVariants.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        className={`product-color-swatch ${
                          selectedColor?.id === color.id ? "active" : ""
                        }`}
                        title={color.color_name}
                        onClick={() => {
                          setSelectedColorId(color.id);

                          if (color.images?.length) {
                            setSelectedMediaType("image");
                            setSelectedImage(color.images[0].image_path);
                          } else if (product.productVideo) {
                            setSelectedMediaType("video");
                            setSelectedImage("");
                          }
                        }}
                      >
                        {color.color_name}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {product.model && (
                <div className="product-model-wrap">
                  <span className="model-label">Brand</span>
                  <span className="model-badge">{product.brand_name}</span>
                  <span className="model-label">Model</span>
                  <span className="model-badge">{product.model}</span>
                </div>
              )}
              <p className={`colors ${isSoldOut ? "sold-out" : "in-stock"}`}>
                {isSoldOut ? "❌ Sold Out" : "✅ In Stock"}
              </p>
              {productVouchers.length > 0 && (
                <div className="product-offer-ticker-wrap">
                  <span className="product-offer-ticker-label">OFFERS</span>

                  <div className="product-offer-ticker-marquee">
                    <div className="product-offer-ticker-track">
                      {voucherTickerText}
                    </div>
                  </div>
                </div>
              )}
              <button
                className="btn btn-general btn-silver"
                role="button"
                disabled={isSoldOut}
                style={{
                  cursor: isSoldOut ? "not-allowed" : "pointer",
                  opacity: isSoldOut ? 0.6 : 1,
                }}
                onClick={() => {
                  if (isSoldOut) return;

                  console.log("SingleProduct product:", product);
                  console.log("SingleProduct vendor_id:", product?.vendor_id);
                  console.log("SingleProduct vendorId:", product?.vendorId);

                  addToCart({
                    id: product.id,
                    barCode: product.barCode,
                    vendor_id: product.vendor_id,
                    productName: product.productName,
                    active_price: product.active_price,
                    image_path:
                      selectedImage || visibleImages?.[0]?.image_path || "",
                    variant: selectedColor?.color_name || "Default",
                    quantity: selectedQty,
                  });
                }}
              >
                {isSoldOut ? "Out of Stock" : "Add To Cart"}
              </button>
            </div>

            <div className="col-md-12">
              <div className="service-h-tab">
                <nav className="nav nav-tabs" id="myTab" role="tablist">
                  <a
                    className="nav-item nav-link active"
                    id="nav-home-tab"
                    data-toggle="tab"
                    href="#nav-home"
                    role="tab"
                    aria-controls="nav-home"
                    aria-expanded="true"
                  >
                    Reviews
                  </a>
                  <a
                    className="nav-item nav-link"
                    id="nav-profile-tab"
                    data-toggle="tab"
                    href="#nav-profile"
                    role="tab"
                    aria-controls="nav-profile"
                  >
                    Description
                  </a>
                </nav>

                <div className="tab-content" id="nav-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="nav-home"
                    role="tabpanel"
                    aria-labelledby="nav-home-tab"
                  >
                    {reviewsLoading ? (
                      <div className="review-carousel-empty">
                        Loading reviews...
                      </div>
                    ) : reviews.length === 0 ? (
                      <div className="review-carousel-empty">
                        No reviews available for this product yet.
                      </div>
                    ) : (
                      <div className="review-carousel-section">
                        <div className="review-carousel-wrap">
                          <button
                            type="button"
                            className="review-nav-btn"
                            onClick={handlePrevReview}
                            disabled={!canGoPrevReview}
                          >
                            &#8592;
                          </button>

                          <div className="review-carousel-viewport">
                            <div className="review-carousel-list">
                              {visibleReviews.map((review) => (
                                <div className="review-card" key={review.id}>
                                  <div className="review-card-top">
                                    <img
                                      src={`${FILE_BASE_URL}/uploads/subscribers/${review.userImage}`}
                                      className="review-card-avatar"
                                      alt="Reviewer"
                                    />

                                    <div className="review-card-user">
                                      <div className="review-card-name">
                                        {review.buyerName || "Anonymous"}
                                      </div>
                                      <div className="review-card-stars">
                                        {renderStars(review.rating)}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="review-card-title">
                                    {getReviewTitle(review)}
                                  </div>

                                  <div className="review-card-description">
                                    {review.comment ||
                                      "No written comment provided."}
                                  </div>

                                  {review.media?.length > 0 && (
                                    <div className="review-card-media-list">
                                      {review.media.map((media) =>
                                        media.mediaType === "photo" ? (
                                          <img
                                            key={media.id}
                                            src={`${FILE_BASE_URL}${media.filePath}`}
                                            alt={
                                              media.originalName ||
                                              "Review media"
                                            }
                                            className="review-card-media-image"
                                          />
                                        ) : (
                                          <video
                                            key={media.id}
                                            controls
                                            className="review-card-media-video"
                                          >
                                            <source
                                              src={`${FILE_BASE_URL}${media.filePath}`}
                                            />
                                          </video>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="review-nav-btn"
                            onClick={handleNextReview}
                            disabled={!canGoNextReview}
                          >
                            &#8594;
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="nav-profile"
                    role="tabpanel"
                    aria-labelledby="nav-profile-tab"
                  >
                    <ul className="product-long-description-list">
                      {product.longDescription
                        ?.split("\n")
                        .map((line, index) =>
                          line.trim() ? <li key={index}>{line}</li> : null,
                        )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-12">
              <div className="related-products-section">
                <h3 className="related-products-title">RELATED PRODUCTS</h3>

                {relatedLoading ? (
                  <div className="related-products-empty">
                    Loading related products...
                  </div>
                ) : relatedProducts.length === 0 ? (
                  <div className="related-products-empty">
                    No related products found.
                  </div>
                ) : (
                  <div className="related-products-slider-wrap">
                    <button
                      type="button"
                      className="related-nav-btn"
                      onClick={handlePrevRelated}
                      disabled={!canGoPrev}
                    >
                      &#8592;
                    </button>

                    <div
                      className="related-products-viewport"
                      onTouchStart={handleRelatedTouchStart}
                      onTouchMove={handleRelatedTouchMove}
                      onTouchEnd={handleRelatedTouchEnd}
                    >
                      <div className="related-products-list">
                        {visibleRelatedProducts.map((item) => (
                          <div
                            key={item.id}
                            className="related-product-card"
                            onClick={() => goToProduct(item.id)}
                          >
                            <div className="related-product-image-wrap">
                              <img
                                src={`${PRODUCT_FILE_BASE}/uploads/products/${item.id}/${item.image_path}`}
                                alt={item.productName}
                                className="related-product-image"
                              />
                            </div>

                            <div className="related-product-content">
                              <h4 className="related-product-name">
                                {item.productName}
                              </h4>
                              <p className="related-product-price">
                                RM {formatCurrency(item.active_price)}
                              </p>

                              <button
                                type="button"
                                className="btn btn-general btn-silver related-cart-btn"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  addToCart({
                                    id: item.id,
                                    barCode: item.barCode,
                                    vendor_id: item.vendor_id,
                                    productName: item.productName,
                                    active_price: item.active_price,
                                    image_path: item.image_path,
                                    variant: item.variant || "Default",
                                  });
                                }}
                              >
                                Add To Cart
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="related-nav-btn"
                      onClick={handleNextRelated}
                      disabled={!canGoNext}
                    >
                      &#8594;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SingleProduct;
